import * as Config from "../../config";
import { DeviceTypes, DeviceConfig, HardwareDeviceConfig, DefaultActuatorPosition } from "../../config/documents/HardwareConfig";
import PoolDevice from "./base";
import { DisplayProcessingLogics, StatesToDisplayItem, PoolDeviceCriticalDesiredActual, PoolDeviceCriticalFieldPropertyAndValue, DeviceState } from "../Types";
import { PoolDeviceInterface } from "../Interfaces";
import Locale from "../../utils/Locale";
import { ValueAndUnit } from "../../types";
import { noValueSign } from '../../types';


export enum criticalFieldIdentifiers {
    PositionPercentLeft = "PositionPercentLeft",
}

const actuatorFriendlyStates = {
    ATTEMPTING_TO_MOVE: "ATTEMPTING_TO_MOVE",
    CALIBRATING: "CALIBRATING",
    CONTINUALLY_TURNING: "CONTINUALLY_TURNING",
    FAILED_TO_TURN: "FAILED_TO_TURN",
    IDLE: "IDLE",
    OVERLOAD: "OVERLOAD",
    SECONDARY_OFFLINE: "SECONDARY_OFFLINE",
    WAITING_TO_MOVE: "WAITING_TO_MOVE",
}

export default class PoolDeviceActuatorTwoWay extends PoolDevice implements PoolDeviceInterface {
    static CanBeAddedToflow = true;
    static DeviceType = DeviceTypes.ACTUATORTWOWAY;
    static Icon = "actuator2";
    static PickerTitle = Locale.gettext("Actuator 2-Way");

    getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue: PoolDeviceCriticalFieldPropertyAndValue[]) {
        // Removing actuator position from the diff in case the actuator is moving
        // Don't warn the user with disagreed states if the actuator is moving.
        const identifiers = super.getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue);
        const friendlyState = this.getDeviceState(PoolsideDaemonDeviceStateNames.FriendlyState)?.value;
        return identifiers.filter(item => (
            item !== criticalFieldIdentifiers.PositionPercentLeft ||
            friendlyState !== actuatorFriendlyStates.ATTEMPTING_TO_MOVE
        ));
    }

    getDeviceState(name: string): DeviceState {
        const state = super.getDeviceState(name);
        // `FriendlyState` did not exist in old Controller version.
        // Mocking it's value by checking state: `Moving` and returning: "ATTEMPTING_TO_MOVE" if `Moving` is true.
        if (name === PoolsideDaemonDeviceStateNames.FriendlyState && state && typeof state.value === "undefined") {
            const value = super.getDeviceState(PoolsideDaemonDeviceStateNames.Moving);
            if (value) {
                return {
                    ...state,
                    value: actuatorFriendlyStates.ATTEMPTING_TO_MOVE
                };
            }
        } else {
            return state;
        }
    }

    getDisplayNameTranslated(displayName:string) {
        switch (displayName) {
        case "FriendlyState": return Locale.gettext("FriendlyState");    
        case "Calibrated Right": return Locale.gettext("Calibrated Right");
        case "Cal. Right": return Locale.gettext("Cal. Right");
        case "Calibrated Left": return Locale.gettext("Calibrated Left");
        case "Cal. Left": return Locale.gettext("Cal. Left");
        case "State": return Locale.gettext("State");
        case "Travel Time": return Locale.gettext("Travel Time");
        case "Travel": return Locale.gettext("Travel");
        default: return super.getDisplayNameTranslated(displayName);
        }
    }

    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        switch(DisplayProcessingLogic) {
        case DisplayProcessingLogics.ACTUATOR_FRIENDLY_STATE:
            switch(value) {
            case actuatorFriendlyStates.IDLE: return { value: Locale.gettext("Idle") };
            case actuatorFriendlyStates.WAITING_TO_MOVE: return { value: Locale.gettext("Waiting") };
            case actuatorFriendlyStates.ATTEMPTING_TO_MOVE: return { value: Locale.gettext("Moving") };
            case actuatorFriendlyStates.CALIBRATING: return { value: Locale.gettext("Calibrating") };
            case actuatorFriendlyStates.OVERLOAD: return { value: Locale.gettext("Overload") };
            case actuatorFriendlyStates.CONTINUALLY_TURNING: return { value: Locale.gettext("Error") };
            case actuatorFriendlyStates.FAILED_TO_TURN: return { value: Locale.gettext("Failed") };
            case actuatorFriendlyStates.SECONDARY_OFFLINE: return { value: Locale.gettext("Offline") };
            default: return { value };
            }
        case DisplayProcessingLogics.ACTUATOR_POSITION:
            if (typeof value === "undefined" || value === null || value === "" || value < 0 || value > 100) {
                return { value: noValueSign };
            }

            const { leftName, rightName } = this.getLeftRightName();

            if (this.supportsPartialMovements()) {
                return { value: `${value}% ${leftName}, ${100-value}% ${rightName}` };
            } else {
                if (value >= 50 ) {
                    return { value: leftName };
                } else {
                    return { value: rightName };
                }
            }
        default: return super.formatDeviceStatusCustom(DisplayProcessingLogic, value);
        }
    }

    isBusy() {
        const friendlyState = this.getDeviceState(PoolsideDaemonDeviceStateNames.FriendlyState)?.value;
        return [
            actuatorFriendlyStates.WAITING_TO_MOVE,
            actuatorFriendlyStates.ATTEMPTING_TO_MOVE,
            actuatorFriendlyStates.CALIBRATING
        ].includes(friendlyState);
    }

    /**
     * Returns left and right friendly name of actuators.
     * (The name of devices that are connect to the left/right output of an actuator)
     * Defaults are 'Left' and 'Right' for three way actuators.
     * 'Open'/'Closed' for two way actuators.
     * @return E.g:
     * {
     *   leftName: 'Pool',
     *   rightName: 'Spa'
     * }
     */
    getLeftRightName = function (): { leftName: string, rightName: string } {
        let leftName:string;
        let rightName:string;
        if (this.deviceConfig.DeviceType == DeviceTypes.ACTUATORTHREEWAY) {
            leftName = Locale.gettext('Left');
            rightName = Locale.gettext('Right');
        } else {
            leftName = Locale.gettext('Open');
            rightName = Locale.gettext('Closed');
        }

        const getFriendlyName = (friendlyNameState: string) => {
            const friendlyName = JSON.parse(friendlyNameState ?? '{}') || {};
            if (friendlyName.UUID) {
                const hardwareConfig = this.allConfigs.Config as Config.HardwareConfig;
                const deviceConfig = hardwareConfig?.Devices?.find(item => item.UUID === friendlyName.UUID)
                    ?? hardwareConfig?.BodiesOfWater?.find(item => item.UUID === friendlyName.UUID)
                    ?? hardwareConfig?.WaterFeatures?.find(item => item.UUID === friendlyName.UUID)
                ;
                if (deviceConfig) {
                    return (friendlyName.Type === 'NOT' ? Locale.gettext('Not') + ' ' : '')
                        + deviceConfig.Name;
                }
            }
        }

        const LeftSideFriendlyName = this.getDeviceState(PoolsideDaemonDeviceStateNames.LeftSideFriendlyName)?.value;
        if (LeftSideFriendlyName) {
            const friendlyName = getFriendlyName(LeftSideFriendlyName);
            if (friendlyName) leftName = friendlyName;
        }

        const RightSideFriendlyName = this.getDeviceState(PoolsideDaemonDeviceStateNames.RightSideFriendlyName)?.value;
        if (RightSideFriendlyName) {
            const friendlyName = getFriendlyName(RightSideFriendlyName);
            if (friendlyName) rightName = friendlyName;
        }

        return ({ leftName, rightName });
    }

    supportsPartialMovements() {
        return this.getDeviceState(PoolsideDaemonDeviceStateNames.SupportsPartialMovements)?.value;
    }

    isActive(): boolean {
        const friendlyState = this.getDeviceState(PoolsideDaemonDeviceStateNames.FriendlyState);
        return [
            actuatorFriendlyStates.WAITING_TO_MOVE,
            actuatorFriendlyStates.ATTEMPTING_TO_MOVE,
            actuatorFriendlyStates.CALIBRATING
        ].includes(friendlyState?.value);
    }

    getConfig(AutoFilterVersion?: number): DeviceConfig {
        if (typeof AutoFilterVersion === "undefined" || AutoFilterVersion >= 25) {
            return this.deviceConfig;
        } else {
            const ret = JSON.parse(JSON.stringify(this.deviceConfig)) as DeviceConfig;
            if (!ret.HardwareDevice) ret.HardwareDevice = {} as HardwareDeviceConfig;

            if (AutoFilterVersion < 25) {
                if (ret.HardwareDevice.ActuatorSettings) {
                    delete ret.HardwareDevice.ActuatorSettings.WaterInletDirection;
                }
            }
            
            if (AutoFilterVersion < 16) {
                if (ret.HardwareDevice.ActuatorSettings) {
                    delete ret.HardwareDevice.ActuatorSettings.MinimumFlowRatePercentLeft;
                    delete ret.HardwareDevice.ActuatorSettings.MinimumFlowRatePercentRight;
                }
            }

            if (AutoFilterVersion < 12) {
                if (ret.HardwareDevice.ActuatorSettings) {
                    ret.HardwareDevice.DefaultActuatorPosition = ret.HardwareDevice.ActuatorSettings.DefaultPosition;
                    delete ret.HardwareDevice.ActuatorSettings.DefaultPosition;
                
                    ret.HardwareDevice.InterlockedActuator = ret.HardwareDevice.ActuatorSettings?.InterlockedActuator;
                    delete ret.HardwareDevice.ActuatorSettings.InterlockedActuator;

                    delete ret.HardwareDevice.ActuatorSettings;
                }
            }

            if (AutoFilterVersion < 10) {
                switch (ret.HardwareDevice.DefaultActuatorPosition) {
                case DefaultActuatorPosition.LEFT:
                    ret.HardwareDevice.DefaultActuatorPosition = DefaultActuatorPosition.RED;
                    break;
                case DefaultActuatorPosition.RIGHT:
                    ret.HardwareDevice.DefaultActuatorPosition = DefaultActuatorPosition.WHITE;
                    break;
                }
            }

            return ret;
        }    
    }

    getCriticalFields(): PoolDeviceCriticalDesiredActual[] {
        let order = 0;
        const fields = [];
    
        {
            const DisplayName = Locale.gettext("Position");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.ACTUATOR_POSITION,
                DisplayProcessingLogicUnitAsDisplayName: false,
                DisplayDiffTimeout: 10,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.PositionPercentLeft,
                ActualProperty: Object.assign({
                    Name: this.siteStates.AutoFilterVersion >= 10 ?
                        PoolsideDaemonDeviceStateNames.ActualPositionPercentLeft :
                        PoolsideDaemonDeviceStateNames.ActualPositionPercentRed
                }, common),
                DesiredProperty: Object.assign({
                    Name: this.siteStates.AutoFilterVersion >= 10 ?
                        PoolsideDaemonDeviceStateNames.DesiredPositionPercentLeft :
                        PoolsideDaemonDeviceStateNames.DesiredPositionPercentRed
                }, common)
            });
        }
    
        return fields;
    }
    
    getInformationFields(): StatesToDisplayItem[] {
        let order = 0;
        const fields = [];
        {
            const common = {
                // Time measured during actuator calibration that was needed for the Actuator to turn from left edge to right edge.
                DisplayName: "Calibrated Left",
                DisplayNameShort: "Cal. Left",
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.MS_TO_S,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.CalibratedMovingLeftToRightTime }, common));
        }
        {
            const common = {
                DisplayName: "Calibrated Right",
                DisplayNameShort: "Cal. Right",
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.MS_TO_S,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.CalibratedMovingRightToLeftTime }, common));
        }
        /*
        {
            const common = {
                DisplayName: "Calibrated",
                DisplayNameShort: "Calibrated",
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.BOOLEAN,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.Calibrated }, common));
        }
        */
        /*
        {
            const common = {
                DisplayName: "Travel Time",
                DisplayNameShort: "Travel",
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.MS_TO_S,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.FullMovementMillisecondsAfterVoltageDetected }, common));
        }
        */
    
        return fields;
    }
    
    
    getMainMetricField(): StatesToDisplayItem {
        return ({
            Name: PoolsideDaemonDeviceStateNames.FriendlyState,
            DisplayName: "State",
            DisplayNameShort: "State",
            DisplayOrder: 1,
            DisplayProcessingLogic: DisplayProcessingLogics.ACTUATOR_FRIENDLY_STATE,
            DisplayProcessingLogicUnitAsDisplayName: false,
        });
    }
    
}

export enum PoolsideDaemonDeviceStateNames {
    ActualPositionPercentLeft = "ActualPositionPercentLeft",
    // AutoFilterVersion < 10
    ActualPositionPercentRed = "ActualPositionPercentRed",
    ActualPowerState = "ActualPowerState",
    Calibrated = "Calibrated",
    CalibratedMovingLeftToRightTime = "CalibratedMovingLeftToRightTime",
    CalibratedMovingRightToLeftTime = "CalibratedMovingRightToLeftTime",
    DesiredPositionPercentLeft = "DesiredPositionPercentLeft",
    // AutoFilterVersion < 10
    DesiredPositionPercentRed = "DesiredPositionPercentRed",
    // AutoFilterVersion >= 13
    DesiredPowerState = "DesiredPowerState",
    ErrorState = "ErrorState",
    ErrorStateStarted = "ErrorStateStarted",
    FriendlyState = "FriendlyState",
    FullMovementMillisecondsAfterVoltageDetected = "FullMovementMillisecondsAfterVoltageDetected",
    LeftSideFriendlyName = "LeftSideFriendlyName",
    // Deprecated. Replaced by ATTEMPTING_TO_MOVE in FriendlyState
    Moving = "Moving",
    PowerState = "PowerState",
    RightSideFriendlyName = "RightSideFriendlyName",
    SupportsPartialMovements = "SupportsPartialMovements",
    Winterized = "Winterized",
}

